import { useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { useTablePrefilterContext } from '@work4all/components/lib/components/table/TablePrefilterProvider';

import { canFlagEntityRecordsAsFavorite } from '@work4all/data/lib/utils/can-flag-entity-records-as-favorite';

import { Work4AllEntity } from '@work4all/models/lib/additionalEnums/Work4AllEntity.entity';
import { SortDirection } from '@work4all/models/lib/DataProvider';
import { Entities } from '@work4all/models/lib/Enums/Entities.enum';
import { ICustomCellConfigBase } from '@work4all/models/lib/table-schema/table-schema';

import { useToggleItemFavorability } from '../../../hooks/use-toggle-Item-favorability';
import {
  isBoardEnabled,
  isCardEnabled,
} from '../table/components/kanban-board/Board';
import { useTableLayoutState } from '../table-layout';
import { IUseDataTableV8Options, useDataTableV8 } from '../use-data-table-v8';

export interface IUseEntityDataTableV8Options<
  TEntity,
  TCustomColumns extends Record<string, ICustomCellConfigBase>
> extends Omit<
      IUseDataTableV8Options<TCustomColumns>,
      'prefilter' | 'layout' | 'tableStateBag'
    >,
    Partial<
      Pick<IUseDataTableV8Options<TCustomColumns>, 'prefilter' | 'layout'>
    > {
  rowModifiers?: (value?: TEntity) => Record<string, boolean>;
  listEntityType?: Entities;
  secondarySort?: {
    field: string;
    direction: SortDirection;
  }[];
}

/**
 * Wrapper for Table entities
 * @param options Look useDataTable
 * @returns useDataTable result
 */
export function useEntityDataTableV8<
  Entity extends Work4AllEntity,
  CustomColumns extends Record<string, ICustomCellConfigBase>
>({
  rowModifiers,
  schema: schemaProps,
  ...options
}: IUseEntityDataTableV8Options<Entity, CustomColumns>) {
  const { prefilter } = useTablePrefilterContext();
  const [layout, setLayout] = useTableLayoutState();
  const canFlagAsFavorite = canFlagEntityRecordsAsFavorite(schemaProps.entity);

  const toggleItemFavorability = useToggleItemFavorability();

  const { t } = useTranslation();

  const schema = useMemo(() => {
    // TODO Fix that
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const columns: any = [...schemaProps.columns];

    if (canFlagAsFavorite) {
      columns.unshift({
        title: t('COMMON.FLAG'),
        accessor: 'favoriteItem.id',
        id: 'favoriteFlag',
        width: 50,
        sortable: false,
        groupable: false,
        quickSearchable: false,
        cell: {
          type: 'Flag',
          params: {
            onClick: (id: string | number, isFavorite: boolean) => {
              toggleItemFavorability({
                id,
                entity: schemaProps.entity,
                isFavorite,
              });
            },
          },
        },
        defaultHidden: false,
        filterable: false,
      });
    }

    return { ...schemaProps, columns };
  }, [schemaProps, canFlagAsFavorite, toggleItemFavorability, t]);

  useEffect(() => {
    if (!isCardEnabled(schema.entity) && layout === 'cards') {
      setLayout('table');
      return;
    }
    if (isBoardEnabled(schema.entity) || layout !== 'board') return;
    // in case for reset to Table where entity does not support board
    setLayout('table');
  }, [layout, schema, setLayout]);

  const resolvedLayout =
    options.layout ??
    (isBoardEnabled(schema.entity) || layout !== 'board' ? layout : 'table');

  const dataTable = useDataTableV8<Entity, CustomColumns>({
    layout: resolvedLayout,
    prefilter,
    schema,
    rowModifiers,

    ...options,
  });

  return { ...dataTable, schema, layout: resolvedLayout };
}
